import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** Hard backstop on the scroll-hold settle window — prevents the viewport from staying pinned. */
  static #SCROLL_HOLD_TIMEOUT_MS = 2500;

  /** Fallback header height (px) when --header-height CSS variable is absent or unparseable. */
  static #FALLBACK_HEADER_HEIGHT_PX = 120;

  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infiniteScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #filterUpdateTimeout;

  /** @type {boolean} rAF latch so scroll-driven URL syncing runs at most once per frame. */
  #urlSyncScheduled = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#cascadePreviousPages();

    // Keep the URL's page param in step with the page the shopper is actually scrolled to. Capture
    // phase so it catches the theme's inner scroll container (body/html are overflow:hidden).
    window.addEventListener('scroll', this.#handleScroll, { capture: true, passive: true });

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infiniteScrollObserver) {
      this.infiniteScrollObserver.disconnect();
    }
    clearTimeout(this.#filterUpdateTimeout);
    window.removeEventListener('scroll', this.#handleScroll, { capture: true });
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * When the shopper deep-links to a page beyond the first (?page=N), sequentially pull in every
   * earlier page — nearest first — until page 1 (and its teaser tiles) is loaded, then land the
   * shopper on the page they actually requested with the earlier pages sitting above it.
   *
   * Done programmatically rather than by scrolling the "previous page" sentinel into view: that
   * sentinel's IntersectionObserver only fires on enter/exit transitions, so a shopper scrolling
   * one direction can never chain past a single previous page. Loading here — one page at a time
   * (await'd, so never a burst of simultaneous section fetches) — guarantees the full run 1…N is
   * present regardless of how the shopper then scrolls.
   *
   * @returns {Promise<void>}
   */
  async #cascadePreviousPages() {
    const { grid, viewMorePrevious, cards } = this.refs;

    // Only when the backward-loading sentinel exists (collection / search infinite scroll — not
    // the forward-only blog), and nothing else already owns the initial scroll position: a hash
    // target (e.g. returning to a specific card from a PDP) or the browser's own restoration.
    if (!grid || !viewMorePrevious) return;
    if (window.location.hash || getScrollTop() > 1) return;
    if (!Array.isArray(cards) || cards.length === 0) return;

    const landedCard = cards[0];
    const landedPage = Number(landedCard?.dataset.page) || 1;
    if (landedPage <= 1) return;

    // Pause the sentinel observer so these programmatic prepends don't race a scroll-triggered one.
    this.infiniteScrollObserver?.disconnect();

    // Read the first product card's page from the live DOM each pass (refs update as we prepend).
    // `guard` bounds the loop by the landed page number in case a fetch stops making progress.
    const firstCardPage = () => Number(grid.querySelector(':scope > [ref="cards[]"]')?.dataset.page) || 1;
    for (let guard = landedPage; firstCardPage() > 1 && guard > 0; guard--) {
      await this.#renderPreviousPage({ updateUrl: false });
      if (!this.isConnected) break;
    }

    // Land on the requested page: the earlier pages are now loaded above it, so scrolling up
    // simply reveals them (including page 1's teasers) with no further fetching required.
    if (landedCard) this.#holdScrollAt(landedCard);

    // Re-arm the sentinels for normal infinite scroll from here on.
    this.#observeViewMore();
  }

  /**
   * Pin `element` to the top of the viewport for a short window after the deep-link cascade,
   * correcting for late layout shifts — disruptor teaser tiles slot into the earlier pages only
   * once their cards have filled, pushing the landed page down after it was first positioned. A
   * ResizeObserver on the grid re-pins exactly when the grid grows (a tile inserted, an image laid
   * out), which a fixed poll would miss if the shift lands after it stops. Bails the instant the
   * shopper scrolls so it never fights their input, with a safety timeout as a hard backstop.
   *
   * @param {Element} element
   * @returns {void}
   */
  #holdScrollAt(element) {
    const grid = this.refs.grid;
    if (!grid) {
      element.scrollIntoView({ block: 'start' });
      return;
    }

    /** @type {ResizeObserver | undefined} */
    let observer;
    /** @type {ReturnType<typeof setTimeout>} */
    let timer;
    const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    const cleanup = () => {
      observer?.disconnect();
      clearTimeout(timer);
      for (const type of events) window.removeEventListener(type, cleanup);
    };

    // A deliberate scroll from the shopper ends the hold immediately — we never fight their input.
    for (const type of events) window.addEventListener(type, cleanup, { once: true, passive: true });

    element.scrollIntoView({ block: 'start' });
    // Re-pin whenever the grid's size changes (a teaser tile slotting in above `element` grows it).
    observer = new ResizeObserver(() => element.scrollIntoView({ block: 'start' }));
    observer.observe(grid);

    // Hard backstop: stop holding after the settle window so we can never trap the viewport.
    timer = setTimeout(cleanup, PaginatedList.#SCROLL_HOLD_TIMEOUT_MS);
  }

  /** Throttles scroll-driven URL syncing to one update per animation frame. */
  #handleScroll = () => {
    if (this.#urlSyncScheduled) return;
    this.#urlSyncScheduled = true;
    requestAnimationFrame(() => {
      this.#urlSyncScheduled = false;
      this.#syncUrlToTopPage();
    });
  };

  /**
   * Reflect the page the shopper is currently scrolled to in the URL's `page` param. Previously the
   * param only changed when a new page loaded (pushState on prepend/append); once every page is
   * present — e.g. after the deep-link cascade in {@link #cascadePreviousPages} — scrolling no
   * longer triggers a load, so without this the address bar would stay frozen on the landed page.
   * Uses replaceState (not push) so scrolling doesn't flood the history stack.
   *
   * @returns {void}
   */
  #syncUrlToTopPage() {
    const { grid } = this.refs;
    if (!grid || !this.isConnected) return;

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards.length) return;

    // The current page is the one filling the most of the viewport (below a line just under the
    // sticky header). Summing visible height per page — rather than reading a single card at the
    // top — handles a multi-column grid whose page boundary falls mid-row, where the top row holds
    // cards from two different pages at once.
    const top = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || PaginatedList.#FALLBACK_HEADER_HEIGHT_PX;
    const viewportBottom = window.innerHeight;
    /** @type {Map<number, number>} */
    const visibleByPage = new Map();
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      const visible = Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, top);
      if (visible <= 0) continue;
      const cardPage = Number(/** @type {HTMLElement} */ (card).dataset.page) || 1;
      visibleByPage.set(cardPage, (visibleByPage.get(cardPage) ?? 0) + visible);
    }

    let page;
    let mostVisible = 0;
    for (const [candidate, area] of visibleByPage) {
      if (area > mostVisible) {
        mostVisible = area;
        page = candidate;
      }
    }
    if (!page) return;

    const url = new URL(window.location.href);
    const currentParam = Number(url.searchParams.get('page')) || 1;
    if (page === currentParam) return;

    // Page 1 is the canonical bare collection URL (no param), matching how the theme links to it.
    if (page <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    url.hash = '';
    history.replaceState(history.state, '', url.toString());
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infiniteScrollObserver) {
      this.infiniteScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infiniteScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infiniteScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    const { liveRegion } = this.refs;
    if (liveRegion) {
      liveRegion.textContent = liveRegion.dataset.labelLoaded ?? '';
      setTimeout(() => { liveRegion.textContent = ''; }, 1000);
    }

    await yieldToMainThread();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * @param {{ updateUrl?: boolean }} [options]
   * @param {boolean} [options.updateUrl=true] - Push the previous page's URL. Suppressed during
   *   the initial deep-link catch-up (see {@link #cascadePreviousPages}) so the address bar keeps
   *   the page the shopper actually landed on rather than cascading back to page 1.
   */
  async #renderPreviousPage({ updateUrl = true } = {}) {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    // Prepending an earlier page shifts the grid's first product backward, so the disruptor
    // tiles (which slot by absolute position via data-disruptor-offset, the index of the first
    // product) need that offset decremented by the number of product cards just prepended.
    // Without this the prepended page's teaser tiles stay hidden — their computed slot lands off
    // the loaded range. Only product cards count toward the offset; disruptor <li>s do not.
    const prependedCards = [...previousPageItemElements].filter((el) => el.matches('[ref="cards[]"]')).length;
    if (prependedCards > 0) {
      const currentOffset = parseInt(grid.getAttribute('data-disruptor-offset') ?? '0', 10) || 0;
      grid.setAttribute('data-disruptor-offset', String(Math.max(0, currentOffset - prependedCards)));
    }

    this.#aspectRatioHelper?.processNewElements();

    const { liveRegion } = this.refs;
    if (liveRegion) {
      liveRegion.textContent = liveRegion.dataset.labelLoaded ?? '';
      setTimeout(() => { liveRegion.textContent = ''; }, 1000);
    }

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    if (updateUrl) history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    // Carry the disruptor teaser tiles across alongside the product cards, so a prepended or
    // appended page brings its merchandising tiles too. They are grid <li>s without ref="cards[]";
    // the extra selector matches nothing on non-collection listings (search/blog), leaving their
    // behaviour unchanged. <disruptor-grid> re-slots the tiles once they are in the live grid.
    return gridElement.querySelectorAll(':scope > [ref="cards[]"], :scope > .collection-grid__disruptor-tile');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Drop the existing infinite-scroll observer. After the filter update lands, the section
    // renderer may create new sentinel elements — the old observer has no reference to them.
    // #observeViewMore() below re-creates the observer with the fresh sentinels.
    this.infiniteScrollObserver?.disconnect();
    this.infiniteScrollObserver = undefined;

    // Wait for the DOM to be updated with the new filtered content, then re-arm.
    // Disconnect on the first mutation so the callback runs exactly once, regardless of whether
    // `data-last-page` changed — both same-page-count and different-page-count filter changes
    // need new sentinel observation and a fresh page-2 pre-fetch.
    const observer = new MutationObserver(() => {
      observer.disconnect();
      clearTimeout(this.#filterUpdateTimeout);
      if (!this.isConnected) return;
      this.#observeViewMore();
      this.#fetchPage('next');
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Fallback: if the mutation never fires (e.g. the element was disconnected and reconnected
      // by the section renderer), re-arm anyway so infinite scroll is never permanently broken.
      this.#filterUpdateTimeout = setTimeout(() => {
        observer.disconnect();
        if (!this.isConnected) return;
        this.#observeViewMore();
        this.#fetchPage('next');
      }, 3000);
    }
  };
}
